// Added By Farhan Khan Khan on 24-Dec-2025
// Extended By System on 29-Jan-2026 - Added Mounting Structure BOM view support
// Fixed By System on 06-Feb-2026 - Added tabs for Standard and Mounting Structure line items
// Updated By System on 11-Apr-2026 - Cleaned up legacy mounting structure logic
import React, { useState, useEffect } from "react";
import { Badge, Button, Col, Container, Row, Spinner, Table } from "react-bootstrap";
import Confirm from "../Confirm";
import { Link, useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import solisPowerApi from "../../api/solisPowerApi";
import Helmet from "react-helmet";
import Main from "../layout/Main";
import { formatDateForView, getUserDetails } from "../../utils/helper";
import PERMISSIONS from "../../constants/permissions";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import PubSub from "pubsub-js";

const BomView = (props) => {
  const userInfo = getUserDetails();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [bom, setBom] = useState(location.state || {});
  const [standardItems, setStandardItems] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchBom(id);
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchBom = async (bomId) => {
    try {
      setLoading(true);
      const result = await solisPowerApi.fetchBomCategoryWithItems(bomId);

      // Verify the result is a valid BOM record (has an ID and not a success:false message)
      if (result && result.id && result.success !== false) {
        setBom(result);
        const allItems = result.items || [];
        setStandardItems([...allItems].sort((a, b) => (a.s_no ?? 9999) - (b.s_no ?? 9999)));
      } else {
        console.warn("[Bom View] BOM not found for ID:", bomId, result);
        setBom({});
        setStandardItems([]);
      }
    } catch (error) {
      console.error("Error fetching BOM:", error);
      setBom({});
      setStandardItems([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteBom = async () => {
    const bomId = bom?.id || (location?.pathname?.split("/").length >= 3 ? location.pathname.split("/")[2] : null);

    if (!bomId) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "BOM ID not found. Cannot delete.",
      });
      return;
    }

    const result = await solisPowerApi.deleteBomCategory(bomId);
    if (result && result.success !== false) {
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "Deleted",
        message: "Record Deleted successfully",
      });
      navigate(`/boms`);
    } else {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: result?.errors || "Failed to delete BOM",
      });
    }
  };

  const editBom = () => {
    navigate(`/boms/${bom.id}/e`, { state: bom });
  };

  const standardHeader = [
    {
      title: "S.No",
      prop: "s_no",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.s_no ?? "-",
    },
    {
      title: "Products",
      prop: "product_name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.product_name ?? row.name ?? "-",
    },
    {
      title: "Specification",
      prop: "specification",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.specification ?? "-",
    },
    {
      title: "Category",
      prop: "category",
      isFilterable: true,
      isSortable: true,
      cell: (row) => (row.category ? String(row.category).replace(/,/g, ", ") : "-"),
    },
    {
      title: "SIZE / RANGE / CAPACITY",
      prop: "size_range_capacity",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.size_range_capacity ?? "-",
    },
    {
      title: "Make",
      prop: "make",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.make ?? "-",
    },
    {
      title: "Unit",
      prop: "unit",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.unit ?? "-",
    },
    {
      title: "Quantity",
      prop: "quantity",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.quantity ?? "-",
    },
    {
      title: "Manufacturer Warranty",
      prop: "manufacturer_warranty",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.manufacturer_warranty ?? "-",
    },
    {
      title: "Brand",
      prop: "brand_name",
      isFilterable: true,
      isSortable: true,
      cell: (row) => row.brand_name ?? "-",
    }
  ];

  const labels = {
    beforeSelect: " ",
  };

  if (loading) {
    return (
      <Main>
        <Container>
          <Row>
            <Col className="text-center mt-5">
              <Spinner animation="border" />
            </Col>
          </Row>
        </Container>
      </Main>
    );
  }

  if (!bom || !bom.id) {
    return (
      <Main>
        <Container>
          <Row className="view-form">
            <Col lg={11} className="pb-3 pt-2">
              <Link className="nav-link" to="/boms">
                Home <i className="fa-solid fa-chevron-right"></i>{" "}
                <div style={{ color: "#23468c", display: "inline" }}>BOM</div>
              </Link>
            </Col>
          </Row>
          <Row className="view-form gx-5 mx-auto">
            <Col lg={9}>
              <Row className="view-form-header bg-custom align-items-center">
                <Col lg={12} className="text-center py-4">
                  <h5>Record Not Found</h5>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </Main>
    );
  }

  return (
    <Main>
      <Container>
        {modalShow && (
          <Confirm
            show={modalShow}
            onHide={() => setModalShow(false)}
            deleteProduct={deleteBom}
            title="Confirm delete?"
            message="You are going to delete the record. Are you sure?"
            table="BOM"
          />
        )}
        <Row className="view-form">
          <Col lg={11} className="pb-3 pt-2">
            <Link className="nav-link" to="/boms">
              Home <i className="fa-solid fa-chevron-right"></i>{" "}
              <div style={{ color: "#23468c", display: "inline" }}>BOM</div>
            </Link>
          </Col>
        </Row>
        <Row className="view-form gx-3">
          <Col xs={12}>
            <Row className="view-form-header bg-custom align-items-center">
              <Col xs={12} md={8}>
                BOM
                <h5>{bom.name}</h5>
              </Col>
              <Col xs={12} md={4} className="d-flex justify-content-end mt-2 mt-md-0">
                {[PERMISSIONS.MODIFY_ALL].some(permission => userInfo?.permissions.map(p => p.name).includes(permission)) && (
                  <Button size="sm" className="btn-edit mx-2" onClick={editBom}>
                    <i className="fa-regular fa-pen-to-square"></i>
                  </Button>
                )}
                {[PERMISSIONS.DELETE_ALL].some(permission => userInfo?.permissions.map(p => p.name).includes(permission)) && (
                  <Button className="btn-sm" variant="danger" onClick={() => setModalShow(true)}>
                    <i className="fa fa-trash-o"></i>
                  </Button>
                )}
              </Col>
            </Row>

            <Row className="py-4 ibs-edit-form">
              <Col xs={12} sm={6} md={6} lg={6}>
                <label>BOM Name</label>
                <span>{bom.name}</span>
              </Col>
              <Col xs={12} sm={6} md={6} lg={3}>
                <label>Status</label>
                <span>
                  <Badge bg={bom.status === "Active" ? "success" : "secondary"}>{bom.status}</Badge>
                </span>
              </Col>
              <Col xs={12} sm={6} md={6} lg={3}>
                <label>Created Date</label>
                <span>{formatDateForView(bom?.createddate || "")}</span>
              </Col>
            </Row>

            <Row className="view-form mt-4">
              <Col xs={12}>
                <Row className="view-form-header bg-custom align-items-center mb-3">
                  <Col xs={12}>
                    <h5>BOM Line Items</h5>
                  </Col>
                </Row>

                <DatatableWrapper
                  body={standardItems}
                  headers={standardHeader}
                  paginationOptionsProps={{
                    initialState: {
                      rowsPerPage: 50,
                      options: [5, 10, 15, 20, 30, 50]
                    },
                  }}
                >
                  <Row className="align-items-end my-2">
                    <Col lg={2} md={4} sm={12}>
                      <Filter classes={{ clearButton: "btn-custom" }} />
                    </Col>
                    <Col md={4} lg={1} sm={12}>
                      <PaginationOptions labels={labels} />
                    </Col>
                  </Row>

                  <div className="table-responsive my-3">
                    <Table striped className="shadow rounded sm my-4">
                      <TableHeader classes={{ th: 'bg-custom text-white sticky-header' }} />
                      {standardItems.length > 0 ? (
                        <TableBody />
                      ) : (
                        <tbody>
                          <tr>
                            <td colSpan={100} align="center">
                              <div className="py-4">No items found.</div>
                            </td>
                          </tr>
                        </tbody>
                      )}
                    </Table>
                  </div>
                  <Pagination classes={{ buttonGroup: "btn-custom", button: "bg-custom border-white" }} />
                </DatatableWrapper>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </Main>
  );
};

export default BomView;
